import { useState, useEffect, useCallback } from 'react';
import {
  getCompetitions,
  getSeasonsByCompetition,
  getTeamsBySeason,
  getMatchesBySeason,
  getMatchdaysBySeason,
} from '../services/serieaApi';

/**
 * Generic async data hook
 */
function useAsyncData(asyncFn, deps = []) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetch = useCallback(async (...args) => {
    setLoading(true);
    setError(null);
    try {
      const result = await asyncFn(...args);
      setData(result);
      return result;
    } catch (err) {
      setError(err.message || 'Errore sconosciuto');
      return null;
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return { data, loading, error, fetch, setData };
}

// ─── Competitions hook ──────────────────────────────────────────────────────
export function useCompetitions() {
  const { data, loading, error, fetch } = useAsyncData(getCompetitions);

  useEffect(() => {
    fetch();
  }, [fetch]);

  return { competitions: data || [], loading, error };
}

// ─── Seasons hook ────────────────────────────────────────────────────────────
export function useSeasons(competitionId) {
  const { data, loading, error, fetch } = useAsyncData(getSeasonsByCompetition);

  useEffect(() => {
    if (competitionId) fetch(competitionId);
  }, [competitionId, fetch]);

  return { seasons: data || [], loading, error };
}

// ─── Teams hook ──────────────────────────────────────────────────────────────
export function useTeams(seasonId) {
  const { data, loading, error, fetch } = useAsyncData(getTeamsBySeason);

  useEffect(() => {
    if (seasonId) fetch(seasonId);
  }, [seasonId, fetch]);

  return { teams: data || [], loading, error };
}

// ─── Matchdays hook ──────────────────────────────────────────────────────────
export function useMatchdays(seasonId) {
  const { data, loading, error, fetch } = useAsyncData(getMatchdaysBySeason);

  useEffect(() => {
    if (seasonId) fetch(seasonId);
  }, [seasonId, fetch]);

  return { matchdays: data || [], loading, error };
}

// ─── Matches hook ────────────────────────────────────────────────────────────
export function useMatches(seasonId, matchDayId) {
  const { data, loading, error, fetch } = useAsyncData(getMatchesBySeason);

  useEffect(() => {
    if (seasonId) fetch(seasonId, { matchDayId });
  }, [seasonId, matchDayId, fetch]);

  return { matches: data || [], loading, error };
}
